import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Address } from '../../../models/address/address';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { RelationshipTypeLabels } from '@models/relationships/relationship-type.enum';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { Guid } from '../../../shared/utils/guid.helper';
import { CompanyChangeAddressComponent } from '../../asic-forms/company';
import { IPDFSection } from "@models/ui-models/pdf-section.interface";
import { Entity } from "@models/companies/entity";
import { ACNPipe } from "../../../shared/pipes/acnPipe";

export class CompanyChangeAddress extends EntityChangeData {
  static override readonly $type = "CompanyChangeAddress";
  address: Address;
  applyToRegistered: boolean;
  applyToPrincipal: boolean;
  relationshipIds: string[];
  occupiesTheAddress: boolean;
  occupierNameIfDoesntOccupy: string;
  authorisedPerson: string; // when ind controller will be implemented, this will be changed to ind

  constructor(data: Partial<CompanyChangeAddress> = {}) {
    super(data);

    this.address = new Address(data.address ?? {});
    this.applyToRegistered = data.applyToRegistered ?? false;
    this.applyToPrincipal = data.applyToPrincipal ?? false;
    this.occupiesTheAddress = data.occupiesTheAddress ?? true;
    this.relationshipIds = data.relationshipIds?.filter(id => Boolean(id) && id !== Guid.EmptyGuid) ?? [];
    this.occupierNameIfDoesntOccupy = data.occupierNameIfDoesntOccupy ?? '';
    this.authorisedPerson = data.authorisedPerson ?? '';
  }

  override toDictionary(entity = new Company() as Entity): { key: string, value: string }[] {
    const company = entity as Company;
    const dict = new ChangeDictionaryHelper();
    const relationships = [...company.officers, ...company.securityholders];
    const usedInFormRelationships = relationships?.filter((r) => this.relationshipIds.includes(r.relationshipId)) ?? [];

    dict
      .add('New Address', this.address.normalizedFullAddress)
      .addYesNo('Registered Office Address', this.applyToRegistered)
      .addYesNo('Principal Place of Business', this.applyToPrincipal);

    if (this.address.normalizedFullAddress && (this.applyToRegistered || this.applyToPrincipal)) {
      dict.addYesNo('Company occupies the premises', this.occupiesTheAddress);
    }

    if (!this.occupiesTheAddress) {
      dict
        .add('Name of Occupier', this.occupierNameIfDoesntOccupy)
        .addYesNo('Does the company have occupiers consent?', !!this.occupierNameIfDoesntOccupy);
    }

    if (this.relationshipIds.length) {
      dict.addRelationshipsIds('Address Apply to:', usedInFormRelationships);
    }

    dict.addDate('Date of Change', this.changeDate);

    return dict.dictionary;
  }

  override prepareToPdf(): IPDFSection[] {
    const sectionDict = new ChangeDictionaryHelper();

    sectionDict
      .addSection('New Address')
      .add('Country', this.address.country)
      .add('Address', this.capitalizeAddress(this.address.normalizedFullAddress))
      .addDate('Effective Date', this.changeDate);

    if (this.address?.normalizedFullAddress) {
      sectionDict
        .addSection('Registered address')
        .addYesNo('Apply change of address?', this.applyToPrincipal);

      if (this.applyToRegistered) {
        if (this.occupiesTheAddress) {
          sectionDict
            .addYesNo('Does the company occupy the premises at the new registered address?', this.occupiesTheAddress);
        } else {
          sectionDict
            .addYesNo('Does the company occupy the premises at the new registered address?', this.occupiesTheAddress)
            .add('Name of Occupier', this.occupierNameIfDoesntOccupy)
            .addYesNo('Does the company have the occupier’s consent?', !!this.occupierNameIfDoesntOccupy);
        }
      }

      sectionDict
        .addSection('Principal Place of Business')
        .addYesNo('Apply change of address?', this.applyToPrincipal);
    }

    return sectionDict.sections;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyChangeAddressComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  capitalizeAddress(address: string): string {
    const australianStates = ['NSW', 'ACT', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT'];

    return address.replace(/\b\w+\b/g, (match) => {
      if (australianStates.includes(match.toUpperCase())) {
        return match.toUpperCase();
      }
      return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
    });
  }

  fillModalWithCompany(modalRef: NgbModalRef, company: Company = new Company(), isEdit = false): NgbModalRef {
    const instance = modalRef.componentInstance as CompanyChangeAddressComponent;

    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.registeredAddress = new Address(company.registeredAddress);
    instance.principalAddress = new Address(company.principalAddress);
    instance.formModel = this;

    // TODO: remove after individualId works fine
    const officerIndividualsIdDictionary: Record<string, string> = {};

    const shareholders = company.currentSecurityholders;

    instance.officersSelectOptions = structuredClone([...company.officers.filter(o => !o.end), ...shareholders])
      .reduce((officersSelectOptions: SelectOptionWithHelperText[], relationship) => {
          // TODO: remove after individualId works fine
          const name = relationship?.individualDataOverride?.fullNameLabel ?? relationship?.entityDataOverride!.name;

          if (!((name) in officerIndividualsIdDictionary)) {
            officerIndividualsIdDictionary[name] = Math.random().toString(36).substring(7);
          }

          // TODO: remove after individualId works fine
          relationship.key = relationship.individualId ?? relationship.relatedEntityId ?? officerIndividualsIdDictionary[relationship?.individualDataOverride?.fullNameLabel ?? relationship.entityDataOverride?.name ?? ''];

          const label = relationship?.individualDataOverride
            ? relationship?.individualDataOverride?.fullNameLabel
            : `${ relationship?.entityDataOverride?.name } ${ ACNPipe.formatValue(relationship?.entityDataOverride?.entityNumber) }`;

          const option: SelectOptionWithHelperText = {
            label: label?.replaceAll('  ', '').trim(),
            value: relationship.key,
            helperText: relationship.individualDataOverride?.address?.normalizedFullAddress ?? relationship.entityDataOverride?.registeredAddress?.normalizedFullAddress ?? ''
          };

          if (!(relationship.key in instance.relationships)) {
            instance.relationships[relationship.key] = [];

            officersSelectOptions?.push(option);
          }

          instance.relationships[relationship.key]?.push(relationship);

          return officersSelectOptions;
        },
        []
      )
      .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()));

    instance.officersSelectOptions = instance.officersSelectOptions.map((relationshipOption) => {
      const optionRelationships = instance.relationships[relationshipOption.value as string];
      const optionTypes = optionRelationships
        .reduce((types: string[], relationship) => {
          const type = RelationshipTypeLabels[relationship.type];

          if (!types.includes(type)) {
            types.push(type);
          }

          return types;
        }, []);

      return {
        label: `${ relationshipOption.label } (${ optionTypes.join(', ') })`,
        value: relationshipOption.value,
        helperText: optionRelationships[0]?.individualDataOverride?.address?.normalizedFullAddress ?? optionRelationships[0]?.entityDataOverride?.registeredAddress?.normalizedFullAddress ?? '',
      };
    });

    return modalRef;
  }

  override prepareToRequest(): CompanyChangeAddress {
    return {
      ...this,
      ...super.prepareToRequest(),
    };
  }
}

export interface SelectOptionWithHelperText extends SelectOption<string> {
  helperText: string;
}

export const poBosString = 'po box';

export function addressIncludesPoBox(address: string) {
  return address
    .toLowerCase()
    .replaceAll('  ', ' ')
    .includes(poBosString);
}

export const poBoxAddressCustomErrors = {
  poBoxAddress: 'Invalid Address: A PO Box or GPO Box cannot be used as a registered address, principal place of business or officeholder address. Please provide a street address.',
};

export const customForm484A1Errors = {
  required: 'You have to pick one of the options above.',
  foreignAddressForCompany: `Changing the company's address to a foreign address is not allowed.`,
  foreignAddressForAllDirectors: `It's now allowed to change address of all directors to a foreign address.`,
  careOfResidential: 'It is not allowed to use Care Of for residential addresses.',
  ...poBoxAddressCustomErrors
};
